import { useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { ApiError, api } from "../../lib/api";
import type { CameraResponse, ChannelType, UpdateCameraRequest } from "../../lib/api";
import styles from "./CameraView.module.css";

export type CameraSurveyProps = {
  camera: CameraResponse;
  onChanged: () => void;
};

// Free-text survey fields the operator may override (nvr linkage stays read-only here).
const TEXT_FIELDS = [
  ["ip_address", "IP address"],
  ["mac_address", "MAC address"],
  ["brand", "Brand"],
  ["model", "Model"],
  ["protocol", "Protocol"],
  ["onvif_profile_main", "ONVIF main"],
  ["onvif_profile_sub", "ONVIF sub"],
] as const;

type TextField = (typeof TEXT_FIELDS)[number][0];
type SurveyForm = { channel_type: ChannelType } & Record<TextField, string>;

const toForm = (camera: CameraResponse): SurveyForm => ({
  channel_type: camera.channel_type as ChannelType,
  ip_address: camera.ip_address ?? "",
  mac_address: camera.mac_address ?? "",
  brand: camera.brand ?? "",
  model: camera.model ?? "",
  protocol: camera.protocol ?? "",
  onvif_profile_main: camera.onvif_profile_main ?? "",
  onvif_profile_sub: camera.onvif_profile_sub ?? "",
});

const diff = (form: SurveyForm, camera: CameraResponse): UpdateCameraRequest => {
  const patch: UpdateCameraRequest = {};
  if (form.channel_type !== camera.channel_type) patch.channel_type = form.channel_type;
  for (const [field] of TEXT_FIELDS) {
    const current = camera[field] ?? "";
    const next = form[field].trim();
    if (next !== current) patch[field] = next === "" ? null : next;
  }
  return patch;
};

const errorMessage = (e: unknown): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't update the survey.";

export const CameraSurvey = ({ camera, onChanged }: CameraSurveyProps) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<SurveyForm>(() => toForm(camera));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof SurveyForm, value: string): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const startEdit = (): void => {
    setForm(toForm(camera));
    setError(null);
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    const patch = diff(form, camera);
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateCamera(camera.id, patch);
      onChanged();
      setEditing(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <span className={styles.panelKicker}>Site survey</span>
          <Button variant="outline" size="sm" onClick={startEdit}>
            Edit survey
          </Button>
        </div>
        <p className={styles.help}>
          Usually auto-discovered by the edge agent. Override here if a value is wrong.
        </p>
        <div className={styles.metaRow}>
          <span>Channel type</span>
          <span className={styles.metaValue}>{camera.channel_type}</span>
        </div>
        <div className={styles.metaRow}>
          <span>IP address</span>
          <span className={styles.metaValue}>{camera.ip_address ?? "—"}</span>
        </div>
        <div className={styles.metaRow}>
          <span>NVR channel</span>
          <span className={styles.metaValue}>{camera.nvr_channel ?? "—"}</span>
        </div>
        <div className={styles.metaRow}>
          <span>MAC</span>
          <span className={styles.metaValue}>{camera.mac_address ?? "—"}</span>
        </div>
        <div className={styles.metaRow}>
          <span>Brand / model</span>
          <span className={styles.metaValue}>
            {[camera.brand, camera.model].filter(Boolean).join(" ") || "—"}
          </span>
        </div>
        <div className={styles.metaRow}>
          <span>Protocol</span>
          <span className={styles.metaValue}>{camera.protocol ?? "—"}</span>
        </div>
        <div className={styles.metaRow}>
          <span>ONVIF main / sub</span>
          <span className={styles.metaValue}>
            {[camera.onvif_profile_main, camera.onvif_profile_sub].filter(Boolean).join(" / ") ||
              "—"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelKicker}>Site survey</span>
        <div className={styles.panelActions}>
          <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="survey-channel-type">
          Channel type
        </label>
        <Select
          id="survey-channel-type"
          value={form.channel_type}
          onChange={(e) => set("channel_type", e.target.value)}
          disabled={busy}
        >
          <option value="ip">ip</option>
          <option value="coax">coax</option>
        </Select>
      </div>

      {TEXT_FIELDS.map(([field, label]) => (
        <div className={styles.field} key={field}>
          <label className={styles.label} htmlFor={`survey-${field}`}>
            {label}
          </label>
          <Input
            id={`survey-${field}`}
            value={form[field]}
            onChange={(e) => set(field, e.target.value)}
            disabled={busy}
          />
        </div>
      ))}

      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
};
